import React from 'react';
import PropTypes from 'prop-types';
import {FileUploadV2} from '@pepsico-ds/ui';
import {
  TextInput,
  TextArea,
  ButtonWithIcon,
  HeaderTitle,
  Divider,
  LanguageTabs
} from '@/components/atoms';
import UploadProgressBar from '@/components/atoms/UploadProgressBar';
import DisplayOrderField from '@/containers/Activity/shared/DisplayOrderField';
import GuidelineModal from './GuidelineModal';
import styles from './VideoDisplayForm.module.scss';
import {isVideoDisplayOrderInvalid} from '@/utils/displayOrderValidation';

const ValidationErrorMessage = ({error, testId}) => {
  if (!error) return null;

  return (
    <div
      className='w-[320px] text-sm text-support-error-primary'
      data-testid={testId}>
      {error}
    </div>
  );
};

const VideoDisplayForm = ({
  languages,
  activeTabIndex,
  setActiveTabIndex,
  descActiveTabIndex,
  setDescActiveTabIndex,
  currentLangData,
  descCurrentLangData,
  updateField,
  updateDescField,
  displayOrder,
  setDisplayOrder,
  handleImageUpload,
  handleImageDelete,
  handleImageFileError,
  handleVideoUpload,
  handleVideoDelete,
  handleVideoFileError,
  showGuidelines,
  setShowGuidelines,
  imageUploadProgress = 0,
  videoUploadProgress = 0,
  imageValidationError = '',
  videoValidationError = ''
}) => {
  return (
    <div
      className='mb-8 rounded-lg bg-[#1f1f1f] shadow-[0_0_24px_rgba(0,0,0,0.08)]'
      data-testid='video-display-form'>
      {/* Section Header */}
      <div className='px-4 py-4'>
        <HeaderTitle
          title='Video Display'
          className='text-base font-bold text-white'
        />
        <p className='text-sm text-[#DADADA]'>
          How the video looks on PepsiConnect Videos
        </p>
      </div>

      {/* Divider */}
      <Divider className='mx-3 my-0 border-0 border-b' />

      {/* Body */}
      <div className='flex flex-col gap-4 px-4 py-3'>
        {/* View Guideline */}
        <ButtonWithIcon
          id='view-guideline'
          iconLeading='open_in_full'
          handleButton={() => setShowGuidelines(true)}
          uiSchema={{
            title: 'View Guideline',
            variant: 'tertiary',
            size: 'small',
            isDark: true
          }}
        />

        {/* Row 1: Image Upload + Name & Tag Description */}
        <div className='flex gap-6'>
          {/* Image Upload Card */}
          <div className='rounded-lg border border-[#4E4E4E] p-3'>
            {/* Image Upload Progress Bar */}
            <UploadProgressBar
              progress={imageUploadProgress}
              processingLabel='Processing image...'
            />
            <FileUploadV2
              accept='.jpg,.png,.webp'
              buttonLabel='Upload file'
              description='Drag and drop your file here (only .webp, .jpg,.png)'
              feedbackType={imageValidationError ? 'error' : 'info'}
              label={{
                label: 'Video Image*',
                showLabelIcon: true,
                infoIconTooltip: {
                  text: 'Max 70 KB'
                }
              }}
              type='image'
              variant={currentLangData.videoImageUrl ? 'edit' : 'default'}
              imageUri={currentLangData.videoImageUrl || undefined}
              uploadIcon='image'
              onUpdate={(files) => handleImageUpload(files)}
              onDelete={() => handleImageDelete()}
              onFileError={(error) => handleImageFileError(error)}
              data-testid='image-upload'
            />
          </div>

          {/* Name + Tag Description */}
          <div className='flex w-[400px] flex-col gap-2'>
            {/* Language Tabs for Name & Tag Description */}
            <LanguageTabs
              languages={languages}
              activeTabIndex={activeTabIndex}
              setActiveTabIndex={(index) => setActiveTabIndex(index)}
              testIdPrefix='name-language-tab'
              dividerClassName='-mt-2 mb-0 border-0 border-b'
            />
            <TextInput
              value={currentLangData.name}
              onChange={(val) => updateField('name', val)}
              label='Name*'
              isDark
              maxLength={55}
              placeholderText='Name'
              id='display-name'
              showLabelIcon
              infoIconTooltip='Max 2 lines (50–55 characters). Extra text will be truncated.'
            />
            <div style={{width: 400, maxWidth: 400}}>
              <TextInput
                value={currentLangData.tagDescription}
                onChange={(val) => updateField('tagDescription', val)}
                label='Tag Description*'
                isDark
                maxLength={85}
                placeholderText='Tag Description'
                id='display-tag-description'
                showLabelIcon
                infoIconTooltip='Max 2 lines (80–85 characters). Extra text will be truncated.'
              />
            </div>
          </div>
        </div>
        <ValidationErrorMessage
          error={imageValidationError}
          testId='image-validation-error'
        />
        {/* Divider */}
        <Divider className='my-0 border-0 border-b' />

        {/* Row 2: Video Upload + Description */}
        <div className='flex gap-6'>
          {/* Video Upload Card */}
          <div className='rounded-lg border border-[#4E4E4E] p-3'>
            {/* Video Upload Progress Bar */}
            <UploadProgressBar
              progress={videoUploadProgress}
              processingLabel='Processing video...'
            />
            <FileUploadV2
              accept='.mp4'
              buttonLabel='Upload file'
              description='Drag and drop your file here (only .mp4)'
              feedbackType={videoValidationError ? 'error' : 'info'}
              label={{
                label: 'Video*',
                showLabelIcon: true,
                infoIconTooltip: {
                  text: '.MP4 | Aspect Radio: 16:9 | Max 100 MB'
                }
              }}
              type='image'
              variant={currentLangData.videoUri ? 'edit' : 'default'}
              imageUri={currentLangData.videoThumbnailUrl || undefined}
              uploadIcon='videocam'
              onUpdate={(files) => handleVideoUpload(files)}
              onDelete={() => handleVideoDelete()}
              onFileError={(error) => handleVideoFileError(error)}
              data-testid='video-upload'
            />
          </div>

          {/* Description */}
          <div className='flex w-[400px] flex-col gap-2 [&_textarea]:!h-[132px] [&_textarea]:!border-[#4E4E4E]'>
            {/* Language Tabs for Description */}
            <LanguageTabs
              languages={languages}
              activeTabIndex={descActiveTabIndex}
              setActiveTabIndex={(index) => setDescActiveTabIndex(index)}
              testIdPrefix='desc-language-tab'
              dividerClassName='-mt-2 mb-0 border-0 border-b'
            />
            <TextArea
              value={descCurrentLangData.description}
              updateValue={(val) => updateDescField('description', val)}
              label='Description'
              uiSchema={{
                isDark: true,
                placeholder: 'Description',
                maxLength: 500,
                showToolbar: true,
                style: {maxHeight: 300, height: 300},
                className: 'mt-2',
                showCharacterCount: true
              }}
              id='display-description'
              trim={false}
            />
          </div>
        </div>
        <ValidationErrorMessage
          error={videoValidationError}
          testId='video-validation-error'
        />
        {/* Divider */}
        <Divider className='my-0 border-0 border-b' />

        {/* Display Order */}
        <div className={styles.displayOrderRow}>
          <DisplayOrderField
            value={displayOrder}
            onUpdate={setDisplayOrder}
            isInvalid={isVideoDisplayOrderInvalid(displayOrder)}
            isInvalidFn={isVideoDisplayOrderInvalid}
          />
        </div>
      </div>

      {/* Guideline Modal */}
      <GuidelineModal
        showGuidelines={showGuidelines}
        setShowGuidelines={setShowGuidelines}
      />
    </div>
  );
};

VideoDisplayForm.propTypes = {
  languages: PropTypes.array.isRequired,
  activeTabIndex: PropTypes.number.isRequired,
  setActiveTabIndex: PropTypes.func.isRequired,
  descActiveTabIndex: PropTypes.number.isRequired,
  setDescActiveTabIndex: PropTypes.func.isRequired,
  currentLangData: PropTypes.object.isRequired,
  descCurrentLangData: PropTypes.object.isRequired,
  updateField: PropTypes.func.isRequired,
  updateDescField: PropTypes.func.isRequired,
  displayOrder: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  setDisplayOrder: PropTypes.func.isRequired,
  handleImageUpload: PropTypes.func.isRequired,
  handleImageDelete: PropTypes.func.isRequired,
  handleImageFileError: PropTypes.func.isRequired,
  handleVideoUpload: PropTypes.func.isRequired,
  handleVideoDelete: PropTypes.func.isRequired,
  handleVideoFileError: PropTypes.func.isRequired,
  showGuidelines: PropTypes.bool.isRequired,
  setShowGuidelines: PropTypes.func.isRequired,
  imageUploadProgress: PropTypes.number,
  videoUploadProgress: PropTypes.number,
  imageValidationError: PropTypes.string,
  videoValidationError: PropTypes.string
};

ValidationErrorMessage.propTypes = {
  error: PropTypes.string,
  testId: PropTypes.string.isRequired
};

export default React.memo(VideoDisplayForm);
